import React, { useState, useEffect } from 'react';
import { useConceptGate } from '../../../context/ConceptGateContext';
import { useFirebase } from '../../../context/FirebaseContext';
import { useTheme } from '../../../context/ThemeContext';
import { useLlmRulesEnabled } from '../../../context/FeatureFlagContext';
import {
  getLibraryRules,
  addUserRule,
  addRuleToPipeline,
  RuleType,
} from '../../../api/conceptgate';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import RuleIcon from '@mui/icons-material/Rule';
import CloseIcon from '@mui/icons-material/Close';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CircularProgress from '@mui/material/CircularProgress';
import RulesHelpModal from './RulesHelpModal';
import logger from '../../../utils/logger';
import './RulesManagement.css';

// Rule presets for different trading styles
const RULE_PRESETS = {
  ftmoDayTrader: {
    name: 'FTMO Day Trader',
    icon: TrendingUpIcon,
    description: 'Intraday trading optimized for FTMO funded accounts',
    rules: [
      'MaxRiskGate',
      'MinRRGate',
      'SpreadGate',
      'MomentumConfirmationGate',
      'EventProximityGate',
      'TimeToCloseGate',
      'LiquidityWindowGate',
      'IntradayAtrTargetGate',
      'VolatilityGate',
    ],
    configs: {
      MaxRiskGate: { maxRiskPercent: 1.0 },
      MinRRGate: { minRR: 1.5 },
      SpreadGate: { maxSpreadTargetPercent: 10 },
      MomentumConfirmationGate: { lookbackCandles: 3, minConfirmingCandles: 2 },
      EventProximityGate: { blockBeforeMin: 5, blockAfterMin: 5 },
      TimeToCloseGate: { minTimeToCloseMinutes: 60, tradingStyle: 'intraday' },
      LiquidityWindowGate: { allowedSessions: ['london', 'newyork'] },
      IntradayAtrTargetGate: { tradingStyle: 'intraday', maxTargetAtrMultiple: 1.5 },
      VolatilityGate: { minVolatilityPercent: 0.08, maxVolatilityPercent: 5.0 },
    }
  },
  ftmoSwingTrader: {
    name: 'FTMO Swing Trader',
    icon: TimelineIcon,
    description: 'Multi-day positions for FTMO Swing accounts',
    rules: [
      'MaxRiskGate',
      'MinRRGate',
      'SpreadGate',
      'TrendAlignmentGate',
      'ExtensionGate',
      'IntradayAtrTargetGate',
      'VolatilityGate',
    ],
    configs: {
      MaxRiskGate: { maxRiskPercent: 0.5 },
      MinRRGate: { minRR: 2.0 },
      SpreadGate: { maxSpreadTargetPercent: 5 },
      TrendAlignmentGate: { timeframe: 'D', period: 200 },
      ExtensionGate: { maPeriod: 20, atrMultiple: 3.0, timeframe: 'D' },
      IntradayAtrTargetGate: { tradingStyle: 'swing', maxTargetAtrMultiple: 3.0 },
      VolatilityGate: { minVolatilityPercent: 0.05, maxVolatilityPercent: 5.0 },
    }
  },
  selfCheck: {
    name: 'Self Check',
    icon: FactCheckIcon,
    description: 'Pre-trade discipline checklist for any trading style',
    rules: [
      'MaxRiskGate',
      'MinRRGate',
      'MomentumConfirmationGate',
      'SessionHighLowGate',
      'EntryProximityGate',
      'TrendAlignmentGate',
    ],
    configs: {
      MaxRiskGate: { maxRiskPercent: 1.0 },
      MinRRGate: { minRR: 1.5 },
      MomentumConfirmationGate: { lookbackCandles: 3, minConfirmingCandles: 2 },
      SessionHighLowGate: { session: 'current', proximityAtrPercent: 0.25 },
      EntryProximityGate: { maxDistanceAtrMultiple: 2.0 },
      TrendAlignmentGate: { timeframe: 'H4', period: 50 },
    }
  }
};

// Helper for rule type display
const getRuleTypeDisplay = (ruleType) => {
  if (ruleType === RuleType.LLM_AGENT || ruleType === 'LLM_AGENT') {
    return 'AI Rule';
  }
  return 'Static Rule';
};

const RulesManagement = ({ open, onClose, libraryRules: externalLibraryRules }) => {
  const { auth } = useFirebase();
  const { isDarkMode } = useTheme();
  const { selectedInstrument, userRules, loadUserRules, subscriptionStatus, activePipeline } = useConceptGate();
  const llmRulesEnabled = useLlmRulesEnabled();
  const [libraryRules, setLibraryRules] = useState(externalLibraryRules || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addingRules, setAddingRules] = useState({}); // Track loading per rule
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState(null); // Track which preset is being applied
  const [showPresets, setShowPresets] = useState(true); // Show presets when user has no rules

  // Calculate Rule Usage
  const maxStaticRules = subscriptionStatus?.maxStaticRules ?? 5;
  const maxAiRules = subscriptionStatus?.maxAiRules ?? 0;

  const staticRuleCount = userRules.filter(r => r.ruleType !== 'LLM_AGENT').length;
  const aiRuleCount = userRules.filter(r => r.ruleType === 'LLM_AGENT').length;

  const canAddStatic = maxStaticRules === -1 || staticRuleCount < maxStaticRules;
  const canAddAi = maxAiRules === -1 || aiRuleCount < maxAiRules;

  // Use external library rules if provided, otherwise load them
  useEffect(() => {
    if (externalLibraryRules && externalLibraryRules.length > 0) {
      setLibraryRules(externalLibraryRules);
      return;
    }

    if (!open || !auth?.currentUser || !selectedInstrument) {
      return;
    }

    const loadLibraryRules = async () => {
      setLoading(true);
      setError(null);

      try {
        await getLibraryRules(auth, (libraryData) => {
          if (libraryData && !libraryData.error) {
            const applicableRules = libraryData.filter(
              (rule) => !rule.instrument || rule.instrument === selectedInstrument
            );
            setLibraryRules(applicableRules);
          } else {
            setError('Failed to load library rules');
          }
          setLoading(false);
        }, { instrument: selectedInstrument });
      } catch (err) {
        logger.error('Error loading rules:', err);
        setError('Failed to load rules');
        setLoading(false);
      }
    };

    loadLibraryRules();
  }, [open, auth, selectedInstrument, externalLibraryRules]);

  const handleAddRule = async (libraryRuleId) => {
    if (!auth?.currentUser || !selectedInstrument) return;

    // Check limits
    const ruleToAdd = libraryRules.find(r => r.id === libraryRuleId);
    if (!ruleToAdd) return;

    const isAi = ruleToAdd.ruleType === 'LLM_AGENT';
    if (isAi && !canAddAi) {
      setError(`Limit reached for AI rules (${maxAiRules}). Upgrade your tier to add more.`);
      return;
    }
    if (!isAi && !canAddStatic) {
      setError(`Limit reached for Static rules (${maxStaticRules}). Upgrade your tier to add more.`);
      return;
    }

    // Set loading for this specific rule
    setAddingRules(prev => ({ ...prev, [libraryRuleId]: true }));
    setError(null);

    try {
      // Use pipeline-specific endpoint if we have an active pipeline
      if (activePipeline?.id) {
        await addRuleToPipeline(auth, (result) => {
          if (result && !result.error) {
            // Refresh user rules from context (async, ensures consistency)
            if (loadUserRules) {
              loadUserRules();
            }
          } else {
            setError('Failed to add rule');
          }
          setAddingRules(prev => ({ ...prev, [libraryRuleId]: false }));
        }, activePipeline.id, {
          libraryRuleId,
          customConfigJson: null,
        });
      } else {
        // Fallback to legacy endpoint (no pipeline)
        await addUserRule(auth, (result) => {
          if (result && !result.error) {
            // Refresh user rules from context (async, ensures consistency)
            if (loadUserRules) {
              loadUserRules();
            }
          } else {
            setError('Failed to add rule');
          }
          setAddingRules(prev => ({ ...prev, [libraryRuleId]: false }));
        }, {
          libraryRuleId,
          // Normalize instrument format to match how rules are loaded (GBPUSD not GBP_USD)
          instrument: selectedInstrument?.replace('_', '').toUpperCase() || selectedInstrument,
          customConfigJson: null,
        });
      }
    } catch (err) {
      logger.error('Error adding rule:', err);
      setError('Failed to add rule: ' + (err.message || 'Unknown error'));
      setAddingRules(prev => ({ ...prev, [libraryRuleId]: false }));
    }
  };

  const isRuleAdded = (libraryRuleId) => {
    return userRules.some((ur) => {
      const urId = typeof ur.libraryRuleId === 'string' ? ur.libraryRuleId : ur.libraryRuleId?.toString();
      const lrId = typeof libraryRuleId === 'string' ? libraryRuleId : libraryRuleId?.toString();
      return urId === lrId;
    });
  };

  // Apply a preset: add all rules from the preset with their configs
  const handleApplyPreset = async (presetKey) => {
    if (!auth?.currentUser || !selectedInstrument) return;

    const preset = RULE_PRESETS[presetKey];
    if (!preset) return;

    setApplyingPreset(presetKey);
    setError(null);

    try {
      // Find library rules matching the preset
      const rulesToAdd = libraryRules.filter(lr =>
        preset.rules.includes(lr.ruleClass) && !isRuleAdded(lr.id)
      );

      // Check limits
      const staticToAdd = rulesToAdd.filter(r => r.ruleType !== 'LLM_AGENT').length;
      const aiToAdd = rulesToAdd.filter(r => r.ruleType === 'LLM_AGENT').length;

      if (maxStaticRules !== -1 && staticRuleCount + staticToAdd > maxStaticRules) {
        setError(`Preset exceeds Static rule limit (${maxStaticRules}). Upgrade to add these rules.`);
        setApplyingPreset(null);
        return;
      }
      if (maxAiRules !== -1 && aiRuleCount + aiToAdd > maxAiRules) {
        setError(`Preset exceeds AI rule limit (${maxAiRules}). Upgrade to add these rules.`);
        setApplyingPreset(null);
        return;
      }

      // Add each rule sequentially
      for (const libraryRule of rulesToAdd) {
        const customConfig = preset.configs[libraryRule.ruleClass] || null;
        await new Promise((resolve, reject) => {
          // Use pipeline-specific endpoint if we have an active pipeline
          if (activePipeline?.id) {
            addRuleToPipeline(auth, (result) => {
              if (result && !result.error) {
                resolve(result);
              } else {
                reject(new Error('Failed to add rule'));
              }
            }, activePipeline.id, {
              libraryRuleId: libraryRule.id,
              customConfigJson: customConfig ? JSON.stringify(customConfig) : null,
            });
          } else {
            // Fallback to legacy endpoint
            addUserRule(auth, (result) => {
              if (result && !result.error) {
                resolve(result);
              } else {
                reject(new Error('Failed to add rule'));
              }
            }, {
              libraryRuleId: libraryRule.id,
              // Normalize instrument format to match how rules are loaded
              instrument: selectedInstrument?.replace('_', '').toUpperCase() || selectedInstrument,
              customConfigJson: customConfig ? JSON.stringify(customConfig) : null,
            });
          }
        });
      }

      // Refresh user rules
      if (loadUserRules) {
        loadUserRules();
      }
      setShowPresets(false);
    } catch (err) {
      logger.error('Error applying preset:', err);
      setError('Failed to apply preset');
    } finally {
      setApplyingPreset(null);
    }
  };

  if (!open) return null;

  // Filter rules: exclude already added and LLM rules if disabled
  const availableRules = libraryRules.filter((lr) => {
    if (isRuleAdded(lr.id)) return false;
    const isLlm = lr.ruleType === RuleType.LLM_AGENT || lr.ruleType === 'LLM_AGENT';
    if (isLlm && !llmRulesEnabled) return false;
    return true;
  });

  return (
    <div className="rules-modal-overlay" onClick={helpModalOpen ? undefined : onClose}>
      <div className="rules-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rules-modal-header">
          <RuleIcon fontSize="small" />
          <span>Add Validation Rules</span>
          <div className="rules-modal-header-actions">
            <button
              className="rules-help-btn"
              onClick={() => setHelpModalOpen(true)}
              title="Learn about validation rules"
            >
              <HelpOutlineIcon fontSize="small" />
            </button>
            <button className="rules-modal-close" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="rules-modal-content">
          {error && (
            <div className="rules-error">{error}</div>
          )}

          {/* Presets Section - show when user has no rules or toggle is on */}
          {selectedInstrument && !loading && showPresets && userRules.length === 0 && (
            <div className="presets-section">
              <div className="presets-header">
                <span className="presets-title">Quick Start Presets</span>
                <span className="presets-subtitle">Choose a trading style to get started</span>
              </div>
              <div className="presets-grid">
                {Object.entries(RULE_PRESETS).map(([key, preset]) => {
                  const IconComponent = preset.icon;
                  const isApplying = applyingPreset === key;
                  return (
                    <button
                      key={key}
                      className={`preset-card preset-${key}`}
                      onClick={() => handleApplyPreset(key)}
                      disabled={applyingPreset !== null}
                    >
                      <IconComponent className="preset-icon" />
                      <span className="preset-name">{preset.name}</span>
                      <span className="preset-description">{preset.description}</span>
                      <span className="preset-rules-count">{preset.rules.length} rules</span>
                      {isApplying && <CircularProgress size={16} className="preset-loading" />}
                    </button>
                  );
                })}
              </div>
              <div className="presets-divider">
                <span>or add rules individually</span>
              </div>
            </div>
          )}

          {/* Toggle to show presets when user already has rules */}
          {selectedInstrument && !loading && userRules.length > 0 && !showPresets && (
            <button
              className="show-presets-btn"
              onClick={() => setShowPresets(true)}
            >
              Show Quick Start Presets
            </button>
          )}

          {!selectedInstrument ? (
            <div className="rules-empty-state">
              Select an instrument to add validation rules
            </div>
          ) : loading ? (
            <div className="rules-loading">
              <CircularProgress size={24} />
              <span>Loading rules...</span>
            </div>
          ) : availableRules.length === 0 ? (
            <div className="rules-empty-state">
              All available rules have been added to your pipeline
            </div>
          ) : (
            <div className="rules-list compact">
              {availableRules.map((libraryRule) => {
                const isAdding = addingRules[libraryRule.id];
                const isLlmRule = libraryRule.ruleType === RuleType.LLM_AGENT || libraryRule.ruleType === 'LLM_AGENT';
                const canAddThis = isLlmRule ? canAddAi : canAddStatic;

                return (
                  <div
                    key={libraryRule.id}
                    className={`rule-item-compact ${isAdding ? 'adding' : ''}`}
                    onClick={() => !isAdding && canAddThis && handleAddRule(libraryRule.id)}
                    title={canAddThis ? "Click to add" : "Limit reached (Upgrade to add more)"}
                    style={{ opacity: canAddThis ? 1 : 0.6, cursor: canAddThis ? 'pointer' : 'not-allowed' }}
                  >
                    <div className="rule-item-content">
                      <div className="rule-item-header">
                        <span className="rule-name">{libraryRule.name}</span>
                        <span className="rule-type-badge">
                          {isLlmRule && <PsychologyIcon fontSize="inherit" />}
                          {getRuleTypeDisplay(libraryRule.ruleType)}
                        </span>
                      </div>
                      <span className="rule-description-inline">{libraryRule.description}</span>
                    </div>
                    <div className="rule-add-action">
                      {isAdding ? (
                        <CircularProgress size={18} />
                      ) : (
                        <AddIcon fontSize="small" color={canAddThis ? 'inherit' : 'disabled'} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Show current rules count */}
        <div className="rules-modal-footer" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>

          {/* Usage Stats Bars */}
          <div className="rules-usage-bars" style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Static Rules</span>
                <span>{staticRuleCount} / {maxStaticRules === -1 ? '∞' : maxStaticRules}</span>
              </div>
              <div style={{ height: '4px', background: '#e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${maxStaticRules === -1 ? 0 : Math.min(100, (staticRuleCount / maxStaticRules) * 100)}%`,
                  background: canAddStatic ? '#4caf50' : '#f44336'
                }} />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>AI Rules</span>
                <span>{aiRuleCount} / {maxAiRules === -1 ? '∞' : maxAiRules}</span>
              </div>
              <div style={{ height: '4px', background: '#e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${maxAiRules === -1 ? 0 : Math.min(100, (aiRuleCount / maxAiRules) * 100)}%`,
                  background: canAddAi ? '#2196f3' : '#f44336'
                }} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="rules-count-info">
              <CheckIcon fontSize="small" />
              {userRules.length} rule{userRules.length !== 1 ? 's' : ''} in pipeline
            </span>
            <button className="rules-done-btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>

      {/* Rules Help Modal */}
      <RulesHelpModal
        isDarkMode={isDarkMode}
        isVisible={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />
    </div>
  );
};

export default RulesManagement;
